import { AtxAttendee, AtxSessionInitConfig, AtxWorkflowStep } from "./atx-sdk";
import { trace } from "./trace";

/**
 * The host's init snippet, as queued on `window.tv.q[0]`:
 * `['init', proctorToken, config]` — though what actually arrives is an
 * `Arguments` object from a host page this client does not control, so it is
 * `unknown` until `parseLegacyArgs` has checked it. (Writing the tuple as a
 * union with `unknown` would document the shape but type-check nothing: in TS
 * that union collapses straight back to `unknown`.)
 */
export type LegacyInitArgs = unknown;

export interface LegacyHostConfig {
  /** Hand-written host snippets pass ids unquoted as often as quoted. */
  profileId?: string | number;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  email?: string;
  additional_properties?: Record<string, unknown>;
  session?: string | number;
  session_type?: string;
  sessionType?: string;
  sessionTitle?: string;
  accommodations?: Record<string, unknown>;
  language?: string;
  initCallback?: (err: unknown, uuid?: string, ...rest: unknown[]) => void;
  errorCallback?: (error: unknown, source?: unknown) => void;
  networkDisconnectionCallback?: () => void;
}

/**
 * What the backend returns on `/v1/session/token` for a session it has mapped
 * to ATX. The two fields are tagged independently, so a session can arrive
 * with a dsn and no flow.
 */
export interface AtxHandoffConfig {
  dsn: string;
  /**
   * The ATX flow this session belongs to. Optional: when the backend has not
   * mapped one, the session id stands in for it (see `mapLegacyConfig`).
   */
  flow_uuid?: string;
}

/**
 * Array-like, not array: the host snippet pushes `arguments` onto the queue
 * (`(i[r].q = i[r].q || []).push(arguments)`), so `q[0]` is an Arguments
 * object. `Array.isArray` is false for it — checking that instead would drop
 * every real host's config and hand ATX an empty identity.
 */
const isLegacyArgs = (value: unknown): value is ArrayLike<unknown> =>
  !!value && typeof value === "object" && typeof (value as ArrayLike<unknown>).length === "number";

/**
 * Ids become ATX identities, so they are narrowed rather than trusted: the
 * host config is whatever a page handed this client. Numbers are kept (host
 * snippets write ids unquoted); anything else is treated as absent.
 */
const asIdentifier = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
};

export const parseLegacyArgs = (
  legacyArgs: LegacyInitArgs
): { proctorToken?: string; hostConfig: LegacyHostConfig } => {
  if (!isLegacyArgs(legacyArgs) || legacyArgs.length < 2) {
    return { hostConfig: {} };
  }

  // Index access rather than destructuring, so this holds for anything
  // array-like regardless of whether it is iterable.
  const proctorToken = legacyArgs[1];
  const hostConfig = legacyArgs[2] as LegacyHostConfig | undefined;

  return {
    proctorToken: typeof proctorToken === "string" ? proctorToken : undefined,
    hostConfig: hostConfig && typeof hostConfig === "object" ? hostConfig : {}
  };
};

/**
 * Host init config → ATX `SessionInitConfig`.
 *
 * Deliberately dropped, because ATX has no equivalent: `session_type` (ATX
 * builds the flow from backend flow config), `previewStyle`,
 * `additionalInstruction` / `referenceLinks` (flow config owns that copy), and
 * every secure-browser policy field — a session enforcing the secure browser
 * is never handed over, so those cannot reach here.
 *
 * The proctor token is not forwarded either: it is this client's credential,
 * and ATX identifies the session by dsn + session id.
 */
export const mapLegacyConfig = (
  legacyArgs: LegacyInitArgs,
  atx: AtxHandoffConfig
): AtxSessionInitConfig => {
  const { hostConfig } = parseLegacyArgs(legacyArgs);

  // `middle_name` and `additional_properties` have no field of their own; the
  // ATX attendee schema is `.passthrough()`, so they survive as extra keys.
  const attendee: AtxAttendee = {
    identifier: asIdentifier(hostConfig.profileId) || undefined,
    first_name: hostConfig.first_name,
    last_name: hostConfig.last_name,
    email: hostConfig.email,
    ...(hostConfig.middle_name ? { middle_name: hostConfig.middle_name } : {}),
    ...(hostConfig.additional_properties ?? {})
  };

  // The session id is always the attempt. It also stands in for the flow when
  // the backend has not mapped a flow_uuid for this token.
  const sessionId = asIdentifier(hostConfig.session);
  if (!sessionId) {
    // ATX would reject this asynchronously, as MISSING_REQUIRED_FIELD, with
    // nothing pointing back at the host snippet that caused it.
    trace("host snippet carries no session id — ATX will be sent an empty identifier", {
      legacyHostConfigKeys: Object.keys(hostConfig)
    });
  }

  // The flow uuid goes in twice on purpose: `identifier` is what ATX resolves
  // the workflow by, and `flow_uuid` is the field its Workflow type carries
  // for the same value.
  const workflowStep: AtxWorkflowStep = {
    identifier: atx.flow_uuid || sessionId,
    ...(atx.flow_uuid ? { flow_uuid: atx.flow_uuid } : {}),
    name: hostConfig.sessionTitle,
    ...(hostConfig.accommodations ? { accommodations: hostConfig.accommodations } : {})
  };

  return {
    dsn: atx.dsn,
    identifier: sessionId,
    attendee,
    workflow_step: workflowStep,
    ...(hostConfig.language ? { language: hostConfig.language } : {})
  };
};
